import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import type { SelectOption } from "@/types/inventory"

interface TagMultiSelectProps {
  tags: SelectOption[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  className?: string
}

function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace("#", "")
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? "#000000" : "#ffffff"
}

export function TagMultiSelect({
  tags,
  selectedIds,
  onChange,
  className,
}: TagMultiSelectProps) {
  const selectedTags = tags.filter((tag) => selectedIds.includes(tag.id))
  const availableTags = tags.filter((tag) => !selectedIds.includes(tag.id))

  function handleSelect(tagId: string) {
    onChange([...selectedIds, tagId])
  }

  function handleRemove(tagId: string) {
    onChange(selectedIds.filter((id) => id !== tagId))
  }

  return (
    <div className={cn("space-y-2", className)}>
      {/* Selected tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm"
              style={{
                backgroundColor: tag.color,
                color: getContrastColor(tag.color || "#6b7280"),
              }}
            >
              {tag.name}
              <button
                type="button"
                onClick={() => handleRemove(tag.id)}
                className="hover:opacity-70 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Available tags */}
      {availableTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {availableTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleSelect(tag.id)}
              className="inline-flex items-center px-2 py-1 rounded-md text-sm border border-dashed border-muted-foreground/50 hover:border-foreground transition-colors"
            >
              <span
                className="w-3 h-3 rounded-full mr-1.5"
                style={{ backgroundColor: tag.color }}
              />
              {tag.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
